import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../components/ToastContext';
import SetupTab from './SetupTab';
import MappingsTab from './MappingsTab';
import ProcessTab from './ProcessTab';
import ReviewTab from './ReviewTab';
import ReorderTab from './ReorderTab';
import HistoryTab from './HistoryTab';
import {
  STORAGE_KEYS, lsGet, lsGetJson, lsSet, buildProductIndex, buildReviewRows,
} from './movementUtils';
import { fetchWarehouses, fetchProducts, fetchMappings, saveMappings } from './movementData';
import { unfinishedRun, getRun } from './movementRuns';
import PageBar from '../../../components/PageBar';
import { useAuth } from '../../../components/AuthContext';

// Regional Product Movement processor.
//
// Two workflows share one screen, exactly as in the standalone tool:
//   A. daily  - handwritten page photos -> OCR -> review -> V2 import CSV
//   B. weekly - system movement report -> consolidate -> reorder import CSV
//
// Warehouses and the product list come from the site's own Warehouses and Item
// Catalog tables, and account mappings are shared through app_settings, so every
// admin works from the same learned map. Page reading runs server-side, so no
// API key is held here. Only the transfer defaults stay in this browser; images
// and parsed rows are session-only.

const TABS = [
  { key: 'setup',    label: 'Setup' },
  { key: 'mappings', label: 'Account Mappings' },
  { key: 'process',  label: 'Process Pages' },
  { key: 'review',   label: 'Review & Export' },
  { key: 'reorder',  label: 'Weekly Reorder' },
  /*
    **The sixth tab, added 8 September 2026 on the owner's word of 5 September.**
    Group G left the other five exactly as the app had them and added this one
    beside them. A sixth is what tips PageBar into giving the tabs a row of
    their own and dropping everything else to the row below, which is rule 26
    doing its job rather than anything being hidden.
  */
  { key: 'history',  label: 'Run History' },
];

export default function MovementProcessor() {
  const toast = useToast();
  const { profile } = useAuth();
  const [tab, setTab] = useState('setup');

  /*
    **The run this sitting is building**, held here because two tabs share it:
    Pages starts it and uploads to it, Review writes its lines and finishes it.
    Null means nothing has been read yet, which is every sitting until somebody
    presses Read pages.
  */
  const [run, setRun] = useState(null);

  /*
    **A run picked back up, 8 September 2026.** The pages are in storage and
    their lines are in the database from the moment they are read, so a closed
    tab is no longer the end of a morning's work. This looks for an unfinished
    run when the screen opens and offers it rather than taking it over: somebody
    may be here to start a new one, and silently loading last week's half-done
    run over the top would be its own kind of surprise.
  */
  const [waiting, setWaiting] = useState(null);   // an unfinished run, offered

  useEffect(() => {
    let alive = true;
    (async () => {
      const { run: found } = await unfinishedRun();
      if (alive && found) setWaiting(found);
    })();
    return () => { alive = false; };
  }, []);

  async function pickUp() {
    const { run: full, lines } = await getRun(waiting.id);
    if (!full) { setWaiting(null); return; }
    setRun({ id: full.id, org_id: full.org_id, name: full.name, ships_from: full.ships_from,
             page_count: full.page_count });
    setReviewRows(lines.map(l => ({
      // Kept since 8 September 2026. Before that it came back blank on every
      // line, and the file made from a picked-up run had no dates in it.
      date: l.written_date || '',
      shorthand: l.written || '',
      destination: l.destination || '',
      itemNumber: l.item_number || '',
      quantity: l.quantity ?? '',
      unit: l.unit || '',
      imageName: '',
      pageId: null,
      pageOrder: l.line_no || 0,
    })));
    setWaiting(null);
    setTab('review');
    toast.success('Picked up ' + full.name + ', ' + lines.length
      + (lines.length === 1 ? ' line' : ' lines') + '.');
  }

  // -- Persisted setup --
  const [config, setConfig] = useState(() => ({
    sourceWarehouse: lsGet(STORAGE_KEYS.src, 'LOU-MAIN'),
    transferredBy: lsGet(STORAGE_KEYS.tb, ''),
  }));
  useEffect(() => {
    lsSet(STORAGE_KEYS.src, config.sourceWarehouse);
    lsSet(STORAGE_KEYS.tb, config.transferredBy);
  }, [config]);

  // -- Shared account mappings --
  const [mappings, setMappings] = useState({});
  const [mapState, setMapState] = useState({ loading: true, saving: false, error: '' });
  // What we believe is currently stored, so an unchanged map never re-saves.
  const lastSavedRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Anything this browser learned before mappings were shared is folded in;
      // the shared copy wins any conflict.
      const local = lsGetJson(STORAGE_KEYS.mappings, {});
      try {
        const remote = await fetchMappings();
        if (!alive) return;
        lastSavedRef.current = JSON.stringify(remote);
        setMappings({ ...local, ...remote });
        setMapState({ loading: false, saving: false, error: '' });
      } catch (e) {
        if (!alive) return;
        // Fall back to the local copy so the tool still works.
        setMappings(local);
        setMapState({ loading: false, saving: false, error: e.message || String(e) });
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (mapState.loading) return undefined;
    const serialized = JSON.stringify(mappings);
    lsSet(STORAGE_KEYS.mappings, serialized); // offline fallback copy
    if (serialized === lastSavedRef.current) return undefined;
    const t = setTimeout(() => {
      setMapState(s => ({ ...s, saving: true }));
      saveMappings(mappings)
        .then(() => { lastSavedRef.current = serialized; setMapState({ loading: false, saving: false, error: '' }); })
        .catch(e => setMapState({ loading: false, saving: false, error: e.message || String(e) }));
    }, 700);
    return () => clearTimeout(t);
  }, [mappings, mapState.loading]);

  // -- Reference data from the site's own tables --
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [refData, setRefData] = useState({ loading: true, error: '' });

  const loadRefData = useCallback(async () => {
    setRefData({ loading: true, error: '' });
    try {
      const [wh, items] = await Promise.all([fetchWarehouses(), fetchProducts()]);
      setWarehouses(wh);
      setProducts(items);
      setRefData({ loading: false, error: '' });
    } catch (e) {
      setRefData({ loading: false, error: e.message || String(e) });
    }
  }, []);

  useEffect(() => { loadRefData(); }, [loadRefData]);

  const productIndex = useMemo(() => buildProductIndex(products), [products]);

  // -- Session state --
  const [images, setImages] = useState([]);
  const [extractedPages, setExtractedPages] = useState([]);
  const [reviewRows, setReviewRows] = useState([]);
  const [reorderRows, setReorderRows] = useState([]);
  const [reorderSkipped, setReorderSkipped] = useState([]);
  const [reorderMeta, setReorderMeta] = useState({ fileName: '', dataRowCount: 0 });

  // Once pages are read and every shorthand resolved, build the review list and
  // move the user to it.
  function handleProcessed(pages, latestMappings) {
    setReviewRows(buildReviewRows(pages, latestMappings));
    setTab('review');
  }

  const reviewCount = reviewRows.length;

  /*
    **The page bar's own row belongs to whichever tab is showing.** Run History
    is three screens in one tab: a list, one run opened, and nothing yet, and
    each hands the bar a different count and a different act. It is handed up
    rather than drawn twice, so there is still one bar (rule 26).
  */
  const [bar, setBar] = useState({});
  // The tabs that hand the bar their own row clear it themselves on the way
  // in. Any other tab gets it emptied here, or it would keep the last one's
  // count and acts.
  const OWNS_BAR = ['history', 'mappings', 'review', 'reorder'];
  useEffect(() => { if (!OWNS_BAR.includes(tab)) setBar({}); }, [tab]); // eslint-disable-line

  return (
    <div>
      {/* Five sub-tabs is right at SUBTAB_SPLIT, so they share tier 3 with the
          page name. Add a sixth and PageBar gives them the row to themselves
          and drops anything else to tier 4 - nothing gets hidden either way. */}
      <PageBar
        /*
          **A tab can name the thing it has open.** Found 8 September 2026:
          an opened run in Run History had NOTHING on the left of the bar, so
          the screen never said which run you were reading. The approved drawing
          has "LOU 2026-09-01, read by Danielle Fontenot on 1 Sep 2026, 08:12".
        */
        title={bar.title || 'Regional Product Movement'}
        subtitle={bar.subtitle}
        subTabs={TABS.map(t => ({
          key: t.key,
          label: t.label,
          count: t.key === 'review' && reviewCount ? reviewCount
            : t.key === 'reorder' && reorderRows.length ? reorderRows.length
            : null,
        }))}
        activeSubTab={tab}
        onSubTabChange={setTab}
        controls={bar.search
          ? <input className="tb-search" placeholder={bar.search.placeholder}
              value={bar.search.value} onChange={e => bar.search.onChange(e.target.value)} />
          : undefined}
        meta={bar.meta}
        /*
          **The filters go behind the Filters button, the acts stay out.** Added
          8 September 2026 after photographing Review at 390: the bar was four
          rows deep, one per control, because everything a tab handed up was an
          action. Group G's drawing has one chip and one button at that width.
        */
        filterCount={bar.filterCount || 0}
        drawer={bar.drawer}
        quiet={bar.quiet}
        actions={bar.actions}
      />

      {/*
        **A run somebody started and did not finish.** Rule 9's shape: a screen
        that quietly forgot a morning's work and a screen with nothing on it
        read the same way. This says which, and what to do about it.
      */}
      {/* Only where a run starts. It was showing above every tab, which pushed
          Account Mappings and the Weekly Reorder down a card's height for a
          notice about something else entirely. */}
      {waiting && !run && (tab === 'setup' || tab === 'process') && (
        <div className="mv-note held">
          <div className="mv-note-title">{waiting.name} was started and not finished</div>
          <div className="mv-note-says">
            Its {waiting.page_count} page{waiting.page_count === 1 ? '' : 's'} were read
            and every line was kept, so nothing has to be read again.
            <span className="mv-note-acts">
              <button className="btn btn-primary" onClick={pickUp}>Pick it up</button>
              <button className="btn btn-quiet" onClick={() => setWaiting(null)}>Leave it</button>
            </span>
          </div>
        </div>
      )}

      {tab === 'setup' && (
        <SetupTab
          config={config} setConfig={setConfig}
          warehouses={warehouses} products={products} mappings={mappings}
          refData={refData} onReloadRefData={loadRefData}
        />
      )}

      {tab === 'mappings' && (
        <MappingsTab mappings={mappings} setMappings={setMappings} warehouses={warehouses}
          status={mapState} toast={toast} setBar={setBar} />
      )}

      {tab === 'process' && (
        <ProcessTab
          warehouses={warehouses}
          mappings={mappings} setMappings={setMappings}
          images={images} setImages={setImages}
          setExtractedPages={setExtractedPages}
          onProcessed={handleProcessed}
          shipsFrom={config.sourceWarehouse}
          profile={profile}
          onRunStarted={setRun}
          toast={toast}
        />
      )}

      {tab === 'review' && (
        <ReviewTab
          config={config} rows={reviewRows} setRows={setReviewRows}
          products={products} productIndex={productIndex}
          warehouses={warehouses}
          /* A picked-up run has no photographs in this browser, so the
             count comes off the run. It read 0 PAGES on a run of five. */
          pageCount={extractedPages.length || run?.page_count || 0}
          run={run}
          onRunFinished={() => setRun(null)}
          onGoToPages={() => setTab('process')}
          setBar={setBar}
          toast={toast}
        />
      )}

      {tab === 'history' && (
        <HistoryTab setBar={setBar} onGoToPages={() => setTab('process')} toast={toast} />
      )}

      {tab === 'reorder' && (
        <ReorderTab
          rows={reorderRows} setRows={setReorderRows}
          skipped={reorderSkipped} setSkipped={setReorderSkipped}
          meta={reorderMeta} setMeta={setReorderMeta}
          products={products} productIndex={productIndex}
          setBar={setBar}
          toast={toast}
        />
      )}
    </div>
  );
}
